import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

export default function SettingsPage() {
  const { user, token, refreshUser } = useAuth();
  const { darkMode, toggleDarkMode } = useTheme();
  const [saturdayEnabled, setSaturdayEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // Target adjustment state
  const [habits, setHabits] = useState([]);
  const [customTargets, setCustomTargets] = useState({});
  const [editingTarget, setEditingTarget] = useState(null);
  const [targetInput, setTargetInput] = useState('');
  const [targetError, setTargetError] = useState(null);

  // Password change state
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState(null);
  const [passwordSaving, setPasswordSaving] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const [settingsRes, habitsRes, targetsRes] = await Promise.all([
        fetch('/api/settings', { headers: { Authorization: `Bearer ${token}` } }),
        fetch('/api/habits', { headers: { Authorization: `Bearer ${token}` } }),
        fetch('/api/habits/targets', { headers: { Authorization: `Bearer ${token}` } }),
      ]);

      if (settingsRes.ok) {
        const data = await settingsRes.json();
        setSaturdayEnabled(data.settings.saturday_enabled);
      }

      if (habitsRes.ok) {
        const data = await habitsRes.json();
        setHabits(data.habits);
      }

      if (targetsRes.ok) {
        const data = await targetsRes.json();
        const targetMap = {};
        data.targets.forEach(t => {
          targetMap[t.habit_id] = t.custom_target;
        });
        setCustomTargets(targetMap);
      }
    } catch (err) {
      console.error('Failed to fetch settings:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const updateSetting = async (key, value) => {
    setSaving(true);
    setMessage(null);
    try {
      const res = await fetch('/api/settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ [key]: value }),
      });
      if (res.ok) {
        const data = await res.json();
        setSaturdayEnabled(data.settings.saturday_enabled);
        setMessage({ type: 'success', text: 'Settings saved!' });
        if (refreshUser) refreshUser();
      } else {
        const err = await res.json();
        setMessage({ type: 'error', text: err.error || 'Failed to save' });
      }
    } catch (err) {
      console.error('Failed to update settings:', err);
      setMessage({ type: 'error', text: 'Network error' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaturdayToggle = () => {
    const newValue = !saturdayEnabled;
    setSaturdayEnabled(newValue);
    updateSetting('saturday_enabled', newValue);
  };

  const handleDarkModeToggle = async () => {
    await toggleDarkMode();
    setMessage({ type: 'success', text: 'Settings saved!' });
    setTimeout(() => setMessage(null), 2000);
  };

  // Target adjustment handlers
  const startEditingTarget = (habitId, currentValue) => {
    setEditingTarget(habitId);
    setTargetInput(currentValue.toString());
    setTargetError(null);
  };

  const cancelEditingTarget = () => {
    setEditingTarget(null);
    setTargetInput('');
    setTargetError(null);
  };

  const saveTarget = async (habitId, habit) => {
    const newTarget = parseInt(targetInput, 10);

    if (isNaN(newTarget) || newTarget < 1) {
      setTargetError('Please enter a valid positive number');
      return;
    }

    if (newTarget < habit.default_min_target) {
      setTargetError(`Target cannot be below minimum of ${habit.default_min_target}`);
      return;
    }

    setSaving(true);
    setTargetError(null);

    try {
      const res = await fetch(`/api/habits/targets/${habitId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ target: newTarget }),
      });

      if (res.ok) {
        setCustomTargets(prev => ({ ...prev, [habitId]: newTarget }));
        setEditingTarget(null);
        setTargetInput('');
        setMessage({ type: 'success', text: 'Target updated!' });
        setTimeout(() => setMessage(null), 2000);
      } else {
        const err = await res.json();
        setTargetError(err.error || 'Failed to update target');
      }
    } catch (err) {
      console.error('Failed to update target:', err);
      setTargetError('Network error');
    } finally {
      setSaving(false);
    }
  };

  // Password change handler
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError(null);

    // Validate passwords
    if (!currentPassword) {
      setPasswordError('Current password is required');
      return;
    }
    if (!newPassword) {
      setPasswordError('New password is required');
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    if (currentPassword === newPassword) {
      setPasswordError('New password must be different from current password');
      return;
    }

    setPasswordSaving(true);

    try {
      const res = await fetch('/api/profile/password', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ currentPassword, newPassword }),
      });

      if (res.ok) {
        setMessage({ type: 'success', text: 'Password changed successfully!' });
        setShowPasswordForm(false);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setTimeout(() => setMessage(null), 3000);
      } else {
        const err = await res.json();
        setPasswordError(err.error || 'Failed to change password');
      }
    } catch (err) {
      console.error('Failed to change password:', err);
      setPasswordError('Network error');
    } finally {
      setPasswordSaving(false);
    }
  };

  const cancelPasswordChange = () => {
    setShowPasswordForm(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError(null);
  };

  // Get daily habits only (for target adjustment)
  const dailyHabits = habits.filter(h => h.frequency === 'daily');

  // Category styling
  const categoryColors = {
    database: { bg: 'bg-green-50 dark:bg-green-900/20', border: 'border-green-200 dark:border-green-800', text: 'text-green-700 dark:text-green-400' },
    grassroots: { bg: 'bg-yellow-50 dark:bg-yellow-900/20', border: 'border-yellow-200 dark:border-yellow-800', text: 'text-yellow-700 dark:text-yellow-400' },
    strategic_partners: { bg: 'bg-blue-50 dark:bg-blue-900/20', border: 'border-blue-200 dark:border-blue-800', text: 'text-blue-700 dark:text-blue-400' },
  };

  const getCategoryStyle = (category) => categoryColors[category] || categoryColors.database;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500 dark:text-gray-400">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl">
      <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-6">Settings</h1>

      {message && (
        <div
          className={`mb-4 p-3 rounded-lg text-sm ${
            message.type === 'success'
              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800'
              : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800'
          }`}
        >
          {message.text}
        </div>
      )}

      <div className="bg-white dark:bg-dark-surface rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
        {/* Saturday Tracking Toggle */}
        <div className="p-5 flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">Saturday Tracking</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              When enabled, Saturday becomes a trackable day for habits. Sunday is always off.
            </p>
          </div>
          <button
            onClick={handleSaturdayToggle}
            disabled={saving}
            className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-dark-bg ${
              saturdayEnabled ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
            } ${saving ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            role="switch"
            aria-checked={saturdayEnabled}
            aria-label="Toggle Saturday tracking"
            data-testid="saturday-toggle"
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform ${
                saturdayEnabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {/* Dark Mode Toggle */}
        <div className="p-5 flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">Dark Mode</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Switch between light and dark themes.
            </p>
          </div>
          <button
            onClick={handleDarkModeToggle}
            disabled={saving}
            className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-dark-bg ${
              darkMode ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
            } ${saving ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            role="switch"
            aria-checked={darkMode}
            aria-label="Toggle dark mode"
            data-testid="dark-mode-toggle"
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform ${
                darkMode ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Target Adjustments Section */}
      <div className="mt-6">
        <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-3">Daily Habit Targets</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Adjust your personal targets above the system minimums. Higher targets earn more XP!
        </p>

        <div className="bg-white dark:bg-dark-surface rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          {dailyHabits.map((habit, index) => {
            const categoryStyle = getCategoryStyle(habit.category);
            const currentTarget = customTargets[habit.id] || habit.default_min_target;
            const isEditing = editingTarget === habit.id;
            const hasCustomTarget = customTargets[habit.id] && customTargets[habit.id] > habit.default_min_target;

            return (
              <div
                key={habit.id}
                className={`p-4 ${index > 0 ? 'border-t border-gray-100 dark:border-gray-700' : ''}`}
                data-testid={`habit-target-${habit.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded ${categoryStyle.bg} ${categoryStyle.text}`}>
                        {habit.category.replace('_', ' ').toUpperCase()}
                      </span>
                      {hasCustomTarget && (
                        <span className="text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 px-2 py-0.5 rounded">
                          Custom
                        </span>
                      )}
                    </div>
                    <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200">{habit.name}</h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Minimum: {habit.default_min_target} | XP per unit: {habit.xp_per_unit}
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    {isEditing ? (
                      <div className="flex flex-col items-end">
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min={habit.default_min_target}
                            value={targetInput}
                            onChange={(e) => setTargetInput(e.target.value)}
                            className="w-20 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-dark-bg text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            disabled={saving}
                            data-testid={`target-input-${habit.id}`}
                          />
                          <button
                            onClick={() => saveTarget(habit.id, habit)}
                            disabled={saving}
                            className="px-2 py-1 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                            data-testid={`save-target-${habit.id}`}
                          >
                            Save
                          </button>
                          <button
                            onClick={cancelEditingTarget}
                            disabled={saving}
                            className="px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
                          >
                            Cancel
                          </button>
                        </div>
                        {targetError && (
                          <span className="text-xs text-red-500 mt-1" data-testid="target-error">{targetError}</span>
                        )}
                      </div>
                    ) : (
                      <>
                        <span className="text-lg font-bold text-gray-800 dark:text-gray-200" data-testid={`current-target-${habit.id}`}>
                          {currentTarget}
                        </span>
                        <button
                          onClick={() => startEditingTarget(habit.id, currentTarget)}
                          className="px-2 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                          data-testid={`edit-target-${habit.id}`}
                        >
                          Edit
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Password Change Section */}
      <div className="mt-6">
        <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-3">Security</h2>

        <div className="bg-white dark:bg-dark-surface rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
          {!showPasswordForm ? (
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">Password</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Change your password to keep your account secure.
                </p>
              </div>
              <button
                onClick={() => setShowPasswordForm(true)}
                className="px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                data-testid="change-password-btn"
              >
                Change Password
              </button>
            </div>
          ) : (
            <form onSubmit={handlePasswordChange} data-testid="password-change-form">
              <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100 mb-4">Change Password</h3>

              {passwordError && (
                <div className="mb-4 p-3 rounded-lg text-sm bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800">
                  {passwordError}
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label htmlFor="current-password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Current Password
                  </label>
                  <input
                    type="password"
                    id="current-password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-bg text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter current password"
                    autoComplete="current-password"
                    data-testid="current-password-input"
                  />
                </div>

                <div>
                  <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    New Password
                  </label>
                  <input
                    type="password"
                    id="new-password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-bg text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter new password (min 8 characters)"
                    autoComplete="new-password"
                    data-testid="new-password-input"
                  />
                </div>

                <div>
                  <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    id="confirm-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-bg text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Confirm new password"
                    autoComplete="new-password"
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>

              <div className="flex items-center gap-3 mt-6">
                <button
                  type="submit"
                  disabled={passwordSaving}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  data-testid="submit-password-change"
                >
                  {passwordSaving ? 'Changing...' : 'Change Password'}
                </button>
                <button
                  type="button"
                  onClick={cancelPasswordChange}
                  disabled={passwordSaving}
                  className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Current user info */}
      <div className="mt-6 p-4 bg-gray-50 dark:bg-dark-surface rounded-lg border border-gray-200 dark:border-gray-700">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Signed in as <span className="font-medium text-gray-700 dark:text-gray-300">{user?.name || user?.email}</span>
        </p>
      </div>
    </div>
  );
}
